import { createApi } from '@reduxjs/toolkit/query/react';
import { authRequest } from '../store/authorizedRequests';
import { AddTemplateParams, DeleteTemplateParams, FetchPdfReportParams, FetchPdfReportResponse, UpdateTemplateParams, WhiteLabelTemplate } from './types';
import { DOWNLOAD_PDF_API_URL, WHITE_LABEL_API_URL } from '../settings/config';

const WHITE_LABEL_REFETCH_TIME = 7200;

export const whiteLabelApi = createApi({
    reducerPath: 'api.whiteLabel',

    keepUnusedDataFor: WHITE_LABEL_REFETCH_TIME,

    tagTypes: ['WhiteLabelTemplates'],

    baseQuery: async (args, api, extraOptions) => {
        const { url, method, body, signal } = args;
        const { dispatch, getState } = api;
        try {
            const data = await authRequest(url, dispatch, getState, {
                method: method,
                body: JSON.stringify(body),
                signal: signal,
            });

            if (typeof data === 'undefined') {
                return { data: null };
            }

            return { data };
        } catch (error) {
            return { error };
        }
    },

    endpoints: (builder) => ({
        fetchTemplates: builder.query<WhiteLabelTemplate[], void>({
            query: (args) => {
                return {
                    method: 'GET',
                    url: `${WHITE_LABEL_API_URL}/white-label/template`,
                };
            },

            providesTags: ['WhiteLabelTemplates'],
        }),

        addTemplate: builder.mutation<WhiteLabelTemplate, AddTemplateParams>({
            query: (args) => {
                return {
                    body: args,
                    method: 'POST',
                    url: `${WHITE_LABEL_API_URL}/white-label/template`,
                };
            },

            invalidatesTags: ['WhiteLabelTemplates'],
        }),

        updateTemplate: builder.mutation<WhiteLabelTemplate, UpdateTemplateParams>({
            query: (args) => {
                const { guid, ...rest } = args;

                return {
                    body: rest,
                    method: 'PATCH',
                    url: `${WHITE_LABEL_API_URL}/white-label/template/${guid}`,
                };
            },

            invalidatesTags: ['WhiteLabelTemplates'],
        }),

        deleteTemplate: builder.mutation<void, DeleteTemplateParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'DELETE',
                    url: `${WHITE_LABEL_API_URL}/white-label/template/${guid}`,
                };
            },

            invalidatesTags: ['WhiteLabelTemplates'],
        }),

        fetchPdfReport: builder.query<FetchPdfReportResponse, FetchPdfReportParams>({
            query: (args) => {
                const { report_guid, report_url, template_guid } = args;

                return {
                    method: 'GET',
                    url: `${DOWNLOAD_PDF_API_URL}?report_url=${report_url}&report_guid=${report_guid}&template_guid=${template_guid}`,
                };
            },
        }),
    }),
});

export const { useFetchTemplatesQuery, useLazyFetchPdfReportQuery, useAddTemplateMutation, useUpdateTemplateMutation, useDeleteTemplateMutation } =
    whiteLabelApi;
